import { useMemo } from 'react';

import { readSemanticColor } from '../../app/color-scheme/color-scheme';
import { usePalette } from '../components/PaletteProvider';
import type { PotentialTone } from '../strategy/model/score-levels';

export type ChartColors = {
    readonly recess: { readonly start: string; readonly middle: string; readonly end: string };
    readonly accent: string;
    readonly axis: string;
    readonly grid: string;
    readonly ink: string;
    readonly inkFaint: string;
    /** The darkest ink, for marks that should read as black. */
    readonly inkStrong: string;
    readonly pocket: string;
    /** The Co Studio automation potential colours. */
    readonly potential: Readonly<Record<PotentialTone, string>>;
    /** The warning status's text colour, the Prioritized column's. */
    readonly statusWarning: string;
    readonly surface: string;
};

const readChartColors = (_colorScheme: 'dark' | 'light'): ChartColors => ({
    accent: readSemanticColor('--semantic-accent', '#ff6a2d'),
    recess: {
        start: readSemanticColor('--semantic-accent-gradient-start', '#ff6a2d'),
        middle: readSemanticColor('--semantic-accent-gradient-middle', '#ff8a5c'),
        end: readSemanticColor('--semantic-accent-gradient-end', '#ffb699'),
    },
    axis: readSemanticColor('--semantic-border-strong', '#c7c7c5'),
    grid: readSemanticColor('--semantic-border-subtle', '#e7e7e7'),
    ink: readSemanticColor('--semantic-text-secondary', '#505052'),
    inkFaint: readSemanticColor('--semantic-text-muted', '#6e6e6c'),
    inkStrong: readSemanticColor('--semantic-text-content-strong', '#353e45'),
    pocket: readSemanticColor('--semantic-surface-subtle', '#f1f1f0'),
    potential: {
        high: readSemanticColor('--semantic-chart-potential-high', 'teal'),
        low: readSemanticColor('--semantic-chart-potential-low', 'steelblue'),
        moderate: readSemanticColor('--semantic-chart-potential-moderate', 'slateblue'),
        none: readSemanticColor('--semantic-chart-potential-none', 'slategray'),
        'very-high': readSemanticColor('--semantic-chart-potential-very-high', 'seagreen'),
    },
    statusWarning: readSemanticColor('--semantic-status-warning-text', '#93370d'),
    surface: readSemanticColor('--semantic-surface-raised', '#ffffff'),
});

/** Semantic colours as literal values a canvas can use, read once per colour scheme rather than per render. */
export const useChartColors = (): ChartColors => {
    const { colorScheme } = usePalette();

    return useMemo(() => readChartColors(colorScheme), [colorScheme]);
};
